import * as React from 'react';

type Dispatch = (action: Action) => void;
interface Action<T = any> {
  type: string;
  data?: T;
}
type State = {
  step: number;
  creating: boolean;
  who: {
    accountId: string;
    patientId: string;
    account: any; // TODO: type
    patient: any; // TODO: type
  };
  what: {
    serviceCode: string;
    subServiceCode: string;
    serviceName: string;
    symptomsRequired: boolean;
    symptoms: string;
  };
  where: {
    addressId: string;
    address: string;
    latitude: number;
    longitude: number;
  };
  when: {
    pcp: boolean;
    date: Date;
    timeSlotId: string;
    timeSlot: string;
  };
};

const CartStateContext = React.createContext<{ state: State; dispatch: Dispatch } | undefined>(undefined);

function cartReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'set_who': {
      /**
       * If the action omits `account` and `patient`, but we already have that
       * data for the patient in state, don't trigger a mutation, since that
       * would clobber the information and require it to be re-fetched.
       * @see SelectPatient.tsx\@onSelectPatient
       */
      const isInitialCall = !action.data.patient && !action.data.account;

      if (isInitialCall && action.data.patientId === state.who.patientId && action.data.accountId === state.who.accountId) {
        return state;
      }

      return { step: 1, who: { ...action.data }, what: {}, where: {}, when: {} } as State;
    }
    case 'set_where': {
      return { ...state, where: { ...action.data }, what: {}, when: {} } as State;
    }
    case 'set_what': {
      return { ...state, what: { ...action.data }, when: {} } as State;
    }
    case 'set_when': {
      return { ...state, when: { ...action.data } };
    }
    case 'set_accountId': {
      return { ...state, who: { ...state.who, accountId: action.data } };
    }
    case 'set_patientId': {
      return { ...state, who: { ...state.who, patientId: action.data } };
    }
    case 'set_step': {
      return { ...state, step: action.data };
    }
    case 'set_creating': {
      return { ...state, creating: action.data };
    }
    case 'reset': {
      return { step: 1, who: {}, what: {}, where: {}, when: {} } as State;
    }
    case 'set_address': {
      const newAddresses = state.who.account.addresses.slice();
      const index = newAddresses.findIndex((a: any) => a.addressType === action.data.addressType);
      if (index > -1) {
        newAddresses[index] = action.data;
      } else {
        newAddresses.push(action.data);
      }

      return { ...state, who: { ...state.who, account: { ...state.who.account, addresses: newAddresses } } };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(cartReducer, { step: 1, who: {}, what: {}, where: {}, when: {} } as State);
  const value = { state, dispatch };
  return <CartStateContext.Provider value={value}>{children}</CartStateContext.Provider>;
}

function useCart() {
  const context = React.useContext(CartStateContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export { CartProvider, useCart };
